import React, { useState } from "react";

import { faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons";

import { PageBanner, Table } from "@performyard/py-components";

import { Divider } from "../../../../components/Divider";
import { AuditLogModal } from "./components/AuditLogModal";
import { BudgetSummaryCard } from "./components/BudgetSummaryCard";
import { GroupHeaderRow } from "./components/GroupHeaderRow";

import type { DecisionRow } from "../../types";
import { DecisionTableContext, MyDecisionsGroupsProps } from "./types";

import { buildGroupRowDataWithExpansion, computeGroupBudgetFigures, mapGroupUsersToDecisionRows } from "./utils";

import { buildAuditLogEntries } from "./AuditLog/buildAuditLogEntries";

export const MyDecisionsGroups = ({
	groupedByManagerId,
	refreshingGroupIds,
	userID,
	expandedRowIdsByGroup,
	baseGridContext,
	onExpand,
	onCollapse,
	recommendedIncreasePercent,
	increaseValues,
	handleSubmitDecisions,
	gridRefs,
	columnDefs,
	notesPayloadKey,
	fullWidthCellRenderer,
	groupCannotBeSubmitted,
	cycleStatus,
}: MyDecisionsGroupsProps): React.ReactElement => {
	const [auditLogManagerId, setAuditLogManagerId] = useState<string | null>(null);

	return (
		<div className="w-[1296px]">
			{[...groupedByManagerId]
				.sort((a, b) => {
					if (a.managerId === userID) return -1;
					if (b.managerId === userID) return 1;
					const nameA = a.users[0]?.manager_name ?? "";
					const nameB = b.users[0]?.manager_name ?? "";
					return nameA.localeCompare(nameB);
				})
				.map((group) => {
					const groupUsers = group.users;
					const groupSubmitted = groupUsers.every((u) => u.status !== "pending");
					const isRefreshingGroup = Boolean(refreshingGroupIds[group.managerId]);
					const groupManagerId = groupUsers[0]?.manager_id ?? null;
					const currentManagerViewing = groupUsers[0]?.current_manager_viewing ?? null;
					const groupManagerName = groupUsers[0]?.manager_name ?? null;

					const baseGroupRowData = mapGroupUsersToDecisionRows(groupUsers, userID).map((row) => ({
						...row,
						notes: baseGridContext?.notesValuesRef?.current?.[String(row.employee_id)] ?? row.notes ?? "",
					}));
					const expandedRowId = expandedRowIdsByGroup[group.managerId] ?? null;
					const groupRowData = buildGroupRowDataWithExpansion(
						baseGroupRowData,
						expandedRowId,
						notesPayloadKey
					);

					const groupGridContext: DecisionTableContext = {
						...baseGridContext,
						managerId: group.managerId,
						expandedRowId,
						onExpand: (rowId: string) => onExpand(group.managerId, rowId),
						onCollapse: () => onCollapse(group.managerId),
					};

					const figures = computeGroupBudgetFigures(
						groupUsers,
						baseGroupRowData,
						increaseValues,
						recommendedIncreasePercent
					);

					const auditLogEntries = buildAuditLogEntries(
						group.managerId,
						baseGroupRowData,
						currentManagerViewing
					);
					const showValidationBanner = String(groupCannotBeSubmitted ?? "") === String(group.managerId);

					return (
						<div key={group.managerId} className="mb-12 mt-6">
							<GroupHeaderRow
								userID={userID}
								groupManagerId={groupManagerId}
								groupManagerName={groupManagerName}
								groupSubmitted={groupSubmitted}
								isRefreshingGroup={isRefreshingGroup}
								onSubmitDecisions={() => handleSubmitDecisions(group.managerId)}
								cycleStatus={cycleStatus}
							/>

							<div className="my-4">
								<Divider color="neutral" />
							</div>

							{showValidationBanner && (
								<div className="my-4">
									<PageBanner
										icon={faTriangleExclamation}
										color="red"
										mainTitle="Please enter an increase for each employee. All increases must be equal to or greater than 0."
									/>
								</div>
							)}
							<div className="pl-4 pr-4 pt-4">
								<BudgetSummaryCard
									figures={figures}
									onViewAuditLog={() => setAuditLogManagerId(group.managerId)}
								/>

								{auditLogManagerId === group.managerId && (
									<AuditLogModal
										onClose={() => setAuditLogManagerId(null)}
										entries={auditLogEntries}
									/>
								)}

								<div className="mt-4 [&_.ag-header]:!bg-white [&_.ag-root-wrapper]:!border-none">
									<Table<DecisionRow>
										ref={(instance) => {
											if (instance) {
												gridRefs.current[group.managerId] = instance;
												return;
											}
											delete gridRefs.current[group.managerId];
										}}
										rowData={groupRowData}
										columnDefs={columnDefs}
										context={groupGridContext}
										autoHeight
										animateRows={true}
										getRowId={(params) => params.data._id}
										rowHeight={48}
										headerHeight={48}
										fullWidthCellRenderer={fullWidthCellRenderer}
										isFullWidthRow={(params) => params.rowNode.data?.superRow === true}
										getRowHeight={(params) => (params.data?.superRow ? 220 : 48)}
									/>
								</div>
							</div>
						</div>
					);
				})}
		</div>
	);
};
